<template>
  <div
    ref="elRef"
    class="FormSegmentedControl"
  >
    <div
      ref="highlightRef"
      class="FormSegmentedControl__highlight"
    />
    <label
      v-for="item in items"
      :key="item.label"
      ref="itemRefs"
      :id="`formSegmentedControl__itemLabel--${item.name}`"
      class="FormSegmentedControl__item"
      data-testid="segmentedControlItem"
      :aria-label="item.ariaLabel"
      :data-value="item.value"
    >
      <input
        v-model="modelValue"
        class="FormSegmentedControl__itemInput"
        type="radio"
        :value="item.value"
        :aria-labelledby="`formSegmentedControl__itemLabel--${item.name}`"
        @input="haptic()"
      >
      {{ item.label }}
    </label>
  </div>
</template>

<script lang="ts" setup>
import haptic from "@/lib/utils/platform/haptic";
import { nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { FormSegmentedControlItem } from "./FormSegmentedControl.types";

const modelValue = defineModel<number | null>({
  required: true,
});

defineProps<{
  items: Array<FormSegmentedControlItem<number | null>>,
}>();

const elRef = useTemplateRef("elRef");
const highlightRef = useTemplateRef("highlightRef");
const itemRefs = useTemplateRef("itemRefs");

const elX = ref(0);

onMounted(() => {
  nextTick(handleResize);
});

watch(modelValue, () => {
  updateHighlightPosition();
});

async function updateHighlightPosition (options: { shouldAnimate: boolean } = {
  shouldAnimate: true,
}) {
  const selectedItemRef = itemRefs.value?.find((itemRef) => {
    return parseFloat(itemRef.dataset.value || "0") === (modelValue.value || 0);
  });

  if (!selectedItemRef || !highlightRef.value) return;

  const {
    x,
    width,
  } = selectedItemRef.getBoundingClientRect();

  const transform = `translateX(${x - elX.value}px) scaleX(${width / 100})`;

  if (options.shouldAnimate) {
    highlightRef.value.animate([
      {
        transform,
      },
    ], {
      duration: 250,
      easing: "ease",
      fill: "forwards",
    });
  } else {
    highlightRef.value.style.transform = transform;
  }
}

function handleResize () {
  if (!elRef.value) return;

  elX.value = elRef.value.getBoundingClientRect().x;

  updateHighlightPosition({
    shouldAnimate: false,
  });
}
</script>

<style lang="scss" scoped>
.FormSegmentedControl {
  --outlineColorDisabled: var(--themeColorNeutral400);
  --backgroundColorDisabled: var(--themeColorNeutral0);
  --textColorDisabled: var(--themeDarkTransparent500);
  position: relative;

  display: grid;
  grid-auto-columns: max(min-content, 1fr);
  grid-auto-flow: column;
  overflow: hidden;

  border-radius: 6px;
  background-color: var(--themeColorWhite);

  transition: background-color 350ms;

  &::after {
    position: absolute;
    inset: 0;

    border: solid 2px var(--themeColorNeutral900);
    border-radius: inherit;

    pointer-events: none;
    transition: border-color 350ms;

    content: "";
  }

  &:has(:disabled) {
    background-color: var(--backgroundColorDisabled);

    &::after {
      border-color: var(--outlineColorDisabled);
    }
  }
}

.FormSegmentedControl__highlight {
  position: absolute;
  top: 0;
  left: 0;
  transform: translateX(0) scaleX(0);
  transform-origin: top left;

  width: 100px;
  height: 100%;

  background-color: var(--themeColorSecondary);

  transition: 350ms background-color;

  .FormSegmentedControl:has(:disabled) & {
    background-color: var(--themeColorNeutral150);
  }
}

.FormSegmentedControl__item {
  position: relative;

  display: grid;
  place-items: center;
  height: 48px;
  padding-inline: 12px;
  padding-top: var(--fontSafariOffset);

  font: var(--fontWeightNormal) 16px / 1 var(--fontFamilyMono);
  white-space: nowrap;

  transition: 350ms;
  transition-property: font-weight, color;

  cursor: pointer;

  &:first-of-type {
    border-radius: 6px 0 0 6px;
  }

  &:last-of-type {
    border-radius: 0 6px 6px 0;
  }

  &::before {
    position: absolute;
    inset-block: 0;
    left: 0;

    border-left: solid 1px var(--themeColorNeutral100);

    transition: opacity 350ms;

    content: "";
  }

  &:has(:checked) {
    color: var(--themeColorSecondaryForeground);
    font-weight: var(--fontWeightMedium);
  }

  &:has(:disabled) {
    color: var(--textColorDisabled);
  }

  &:has(:checked)::before,
  &:has(:checked) + &::before {
    opacity: 0;
  }
}

.FormSegmentedControl__itemInput {
  position: absolute;
  top: 0;
  left: 0;

  opacity: 0;

  pointer-events: none;
}
</style>
